import { useContext, useEffect, useState } from "react"
import { useColorScheme } from '@mui/material/styles'
import { BrowserRouter, Navigate, Routes, Route } from "react-router-dom"

import BillingPaymentCard from '/pages/billing-payment-card'
import BillingDirectDebit from '/pages/billing-direct-debit'
import BillingInvoices from '/pages/billing-invoices'
import BillingPlan from '/pages/billing-plan'
import SupportHistory from '/pages/support-history'
import SupportThread from './pages/support-thread'
import SupportForm from '/pages/support-form'
import Dashboard from '/pages/dashboard'
import Loading from '/pages/loading'
import Login from '/pages/login'
import ResetPassword from '/pages/reset-password'
import AcceptInvitation from '/pages/accept-invitation'
import PassDetails from '/pages/pass-details'
import PassNotifications from '/pages/pass-notifications'
import PassEditor from '/pages/pass-editor'
import PassScannerHistory from '/pages/pass-scans'
import PassInstaller from '/pages/pass-installer'
import DownloadScannerApps from '/pages/download-scanner-apps'
import CreateNewTemplate from '/pages/create-new-template'
import TemplateList from '/pages/template-list'
import TemplateDetails from '/pages/template-details'
import TemplateIssuedPasses from '/pages/template-issued-passes'
import TemplateDuplicator from '/pages/template-duplicator'
import TemplateWizard from '/pages/template-wizard'
import TemplateEditor from '/pages/template-editor'
import TemplateStats from '/pages/template-stats'
import TemplateScanReportDates from '/pages/template-scan-report-dates'
import TemplateScanReport from './pages/template-scan-report'
import TemplateIssuePasses from '/pages/template-issue-passes'
import AccountManagementDetails from '/pages/account-management-details'
import AccountManagementContact from '/pages/account-management-contact'
import AccountManagementUsers from '/pages/account-management-users'
import AccountManagementDelete from '/pages/account-management-delete'
import AccountManagementPassword from '/pages/account-management-password'
import AccountManagementAPIKeys from '/pages/account-management-api-keys'
import ActivePasses from '/pages/active-passes'
import Statistics from '/pages/statistics'
import Onboarding from '/pages/onboarding'
import { AppContext } from "/providers/app-context"

const AppRoutes = ({ client, loading }) => {

  const { mode, systemMode } = useColorScheme()
  const [routes, setRoutes] = useState([<Route key="loading" path="*" element={<Loading />} />])
  const { user } = useContext(AppContext)

  useEffect(() => {
    const themeMode = mode === "system" ? systemMode : mode
    document.body.setAttribute('data-theme', themeMode)
    if (themeMode === 'dark') {
      document.body.classList.add('dark')
    } else {
      document.body.classList.remove('dark')
    }
  }, [mode, systemMode])

  useEffect(() => {
    if (!loading) {
      if (client === null) {
        setRoutes([
          <Route key="reset-password" path="reset-password/:token" element={<ResetPassword/>}/>,
          <Route key="accept-invitation" path="accept-invite/:token" element={<AcceptInvitation/>}/>,
          <Route key="login" path="*" element={<Login />} />,
        ])
      } else if (!client.initialised) {
        setRoutes([
          <Route key="onboarding" path="*" element={<Onboarding />} />,
        ])
      } else {
        setRoutes([
          <Route key="root" path="/">
            <Route index element={<Dashboard />} />,
            <Route path="account-management">
              <Route index element={<Navigate to={user?.permissions.client_admin ? "details" : "password"} replace />} />
              <Route path="details" element={<AccountManagementDetails />} />
              <Route path="contact" element={<AccountManagementContact />} />
              <Route path="users" element={<AccountManagementUsers />} />
              <Route path="api-keys" element={<AccountManagementAPIKeys />} />
              <Route path="password" element={<AccountManagementPassword />} />
              <Route path="delete" element={<AccountManagementDelete />} />
            </Route>,

            <Route path="billing">
              <Route index element={<Navigate to="invoices" replace />} />
              <Route path="invoices" element={<BillingInvoices />} />
              <Route path="payment-card" element={<BillingPaymentCard />} />
              <Route path="direct-debit" element={<BillingDirectDebit />} />
              <Route path="plan" element={<BillingPlan />} />
            </Route>,

            <Route path="create-template" element={<CreateNewTemplate />} />,
            <Route path="active-passes" element={<ActivePasses />} />,
            <Route path="statistics" element={<Statistics />} />,
            <Route path="download-apps" element={<DownloadScannerApps />} />,
 
            <Route path="support">
              <Route index element={<Navigate to="history" replace />} />
              <Route path="history" element={<SupportHistory />} />
              <Route path="thread/:thread_id" element={<SupportThread />} />,
              <Route path="new" element={<SupportForm />} />
            </Route>

            <Route path="templates">
              <Route index element={<TemplateList />} />
              <Route path=":templateId">
                <Route index element={<Navigate to="details" replace />} />
                <Route path="details" element={<TemplateDetails />} />
                <Route path="edit" element={<TemplateEditor />} />
                <Route path="wizard" element={<TemplateWizard />} />
                <Route path="duplicate" element={<TemplateDuplicator />} />
                <Route path="issue-passes" element={<TemplateIssuePasses />} />
                <Route path="stats" element={<TemplateStats />} />

                <Route path="scan-reports">
                  <Route index element={<TemplateScanReportDates />} />
                  <Route path=":ts" element={<TemplateScanReport />} />
                </Route>

                <Route path="passes">
                  <Route index element={<TemplateIssuedPasses />} />
                  <Route path=":passId">
                    <Route index element={<Navigate to="details" replace />} />
                    <Route path="details" element={<PassDetails />} />
                    <Route path="messages" element={<PassNotifications />} />
                    <Route path="edit" element={<PassEditor />} />
                    <Route path="install" element={<PassInstaller />} />
                    <Route path="scans" element={<PassScannerHistory />} />
                  </Route>
                </Route>
              </Route>
            </Route>
          </Route>,

          <Route key="catchall" path="*" element={<Navigate to="/" replace />} />
        ])
      }
    }
  }, [client, loading])




  return (
    <BrowserRouter>
      <Routes>
        {routes}
      </Routes>
    </BrowserRouter>
  )
}

export default AppRoutes